import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Eye,
  Loader2,
  Mail,
  Pencil,
  Receipt,
  Trash2,
} from 'lucide-react'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { SectionInfoIcon } from '@/components/ui/SectionInfoIcon'
import { ROUTE_BUILDERS } from '@/config/routes'
import { formatPaymentLogDateTime } from '@/features/application/utils/applicationFormatters'
import { formatAmount } from '@/lib/utils/formatters'
import { cn } from '@lib/utils/helpers'
import { gatewayMetaLookup } from '../../services/reportsService'
import { ReportTableScroll } from '../ReportTableScroll'
import reportStyles from '../../styles/reportsTable.module.css'
import type { BalanceReportTableProps } from '../../types/components'
import { BALANCE_REPORT_SORT_COLUMNS } from '../../constants/balanceReportPage'
import {
  chargebackDisplay,
  formatDisplayDate,
  padApplicantId,
  paymentLogAmounts,
  paymentLogDetailPath,
} from '../../utils/balanceReportPageHelpers'
import { BALANCE_REPORT_PAYMENT_LOG_NAV_STATE } from '../../utils/paymentLogBackToList'

export function BalanceReportTable({
  loading,
  params,
  rows,
  pageOptions,
  expandedRows,
  canOpenApplication,
  canPerformPaymentActions,
  gatewayMeta,
  adjustmentLoadingKey,
  onSort,
  onPageChange,
  onPerPageChange,
  onToggleExpanded,
  onSendInvoice,
  onOpenPaymentAdjustment,
  canEditPayment,
  canDeletePayment,
  deletePaymentLoadingKey,
  onDeletePayment,
}: BalanceReportTableProps) {
  return (
    <div className={cn(reportStyles.tableCard, 'w-full max-w-full min-w-0')}>
        <ListPaginationBar
          placement="header"
          currentPage={pageOptions.current}
          lastPage={pageOptions.last}
          totalRecords={pageOptions.total}
          perPage={params.perPage}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
          testIdPrefix="balance-report"
          summaryLayout="angular"
        />
        {loading ? (
          <div className="flex min-h-[12rem] items-center justify-center py-16">
            <Loader2
              className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
              aria-hidden
            />
          </div>
        ) : (
          <ReportTableScroll
            enabled={rows.length > 0}
            testIdPrefix="balance-report"
            className="max-w-full"
          >
            <table
              className={cn(
                reportStyles.listingTable,
                reportStyles.balanceReportTable,
              )}
              data-testid="balance-report-table"
            >
              <thead>
                <tr>
                          <th className="w-10" />
                          {BALANCE_REPORT_SORT_COLUMNS.map((col) => {
                            const active = params.orderBy === col.key
                            return (
                              <th key={col.key} className="text-left">
                                <button
                                  type="button"
                                  onClick={() => onSort(col.key)}
                                  className={cn(
                                    'inline-flex items-center gap-1.5 whitespace-nowrap text-left font-semibold text-slate-600 hover:text-slate-900',
                                    active && 'text-[rgb(21_151_255)]',
                                  )}
                                >
                                  <span>{col.label}</span>
                                  <span className={cn('inline-flex items-center ml-1 text-[11px]', active ? 'text-[#1597ff]' : 'text-slate-400')}>
                                    {active ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3 shrink-0" aria-hidden />}
                                  </span>
                                </button>
                              </th>
                            )
                          })}
                          <th className={reportStyles.actionCell}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={BALANCE_REPORT_SORT_COLUMNS.length + 2}
                              className="py-12 text-center text-slate-500"
                            >
                              No records found.
                            </td>
                          </tr>
                        ) : (
                          rows.map((row, index) => {
                            const key = String(
                              row.applicant_id ?? row.application_id ?? index,
                            )
                            const expanded = expandedRows.has(key)
                            const paymentLogs = row.payment_logs ?? []
                            return (
                              <Fragment key={key}>
                                <tr className="border-b border-slate-100">
                                  <td>
                                    <button
                                      type="button"
                                      className="inline-flex rounded p-1 text-slate-600 hover:bg-slate-100"
                                      onClick={() => onToggleExpanded(key)}
                                      disabled={paymentLogs.length === 0}
                                      aria-label={
                                        expanded
                                          ? 'Collapse payment rows'
                                          : 'Expand payment rows'
                                      }
                                    >
                                      {expanded ? (
                                        <ChevronDown
                                          className="h-4 w-4"
                                          aria-hidden
                                        />
                                      ) : (
                                        <ChevronRight
                                          className="h-4 w-4"
                                          aria-hidden
                                        />
                                      )}
                                    </button>
                                  </td>
                                  <td>
                                    {row.applicant_id != null &&
                                    canOpenApplication ? (
                                      <Link
                                        to={ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(
                                          row.applicant_id,
                                        )}
                                        className="text-[#007BFF] hover:underline"
                                      >
                                        {padApplicantId(row.applicant_id)}
                                      </Link>
                                    ) : (
                                      padApplicantId(row.applicant_id) || '—'
                                    )}
                                  </td>
                                  <td>
                                    {[row.first_name, row.last_name]
                                      .filter(Boolean)
                                      .join(' ') || '—'}
                                  </td>
                                  <td>{row.company_name ?? '—'}</td>
                                  <td>{row.property_name ?? '—'}</td>
                                  <td>{formatAmount(row.rent_amount ?? 0)}</td>
                                  <td>
                                    {formatAmount(
                                      row.payment_amount ?? row.amount_paid ?? 0,
                                    )}
                                  </td>
                                  <td>
                                    {formatAmount(
                                      row.payment_refund ?? row.refunds ?? 0,
                                    )}
                                  </td>
                                  <td>
                                    {chargebackDisplay(
                                      row.chargeback_exist ?? row.chargebacks,
                                    )}
                                  </td>
                                  <td>{formatAmount(row.balance_due ?? 0)}</td>
                                  <td>{row.application_status ?? '—'}</td>
                                  <td>
                                    {formatDisplayDate(
                                      row.contract_start_date ?? row.lease_start_date,
                                    )}
                                  </td>
                                  <td>
                                    {formatDisplayDate(
                                      row.payment_date ??
                                        row.last_activity_date ??
                                        row.updated_at,
                                    )}
                                  </td>
                                  <td className={reportStyles.actionCell}>
                                    <div className={reportStyles.actionGroup}>
                                      {row.applicant_id ? (
                                        <>
                                          <span className={reportStyles.actionSlot}>
                                            <button
                                              type="button"
                                              onClick={() => onSendInvoice(row)}
                                              className={reportStyles.actionIconBtn}
                                              title="Send Invoice Mail"
                                              aria-label="Send Invoice Mail"
                                            >
                                              <Mail aria-hidden />
                                            </button>
                                          </span>
                                          <span className={reportStyles.actionSlot}>
                                            <button
                                              type="button"
                                              className={reportStyles.actionIconBtn}
                                              title="Edit"
                                              aria-label="Edit"
                                              onClick={() =>
                                                onOpenPaymentAdjustment(row, key)
                                              }
                                              disabled={adjustmentLoadingKey === key}
                                            >
                                              {adjustmentLoadingKey === key ? (
                                                <Loader2
                                                  className="animate-spin"
                                                  aria-hidden
                                                />
                                              ) : (
                                                <Pencil aria-hidden />
                                              )}
                                            </button>
                                          </span>
                                        </>
                                      ) : (
                                        '—'
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {expanded ? (
                                   <tr
                                     key={`${key}-payments`}
                                     className="bg-slate-100/60"
                                   >
                                     <td />
                                     <td colSpan={BALANCE_REPORT_SORT_COLUMNS.length + 1}>
                                       <div className={reportStyles.nestedTableWrapper}>
                                         <div className={reportStyles.nestedTableBadge}>
                                           <Receipt className="h-3.5 w-3.5 text-[#1597ff]" aria-hidden />
                                           Payment Log Breakdown
                                         </div>
                                         {paymentLogs.length === 0 ? (
                                           <p className="text-xs text-slate-500 italic px-2 py-1">
                                             No payment log transactions found.
                                           </p>
                                         ) : (
                                           <div className={reportStyles.nestedTableCard}>
                                             <table className={reportStyles.nestedTable}>
                                               <thead>
                                                 <tr>
                                                   <th>Type</th>
                                                   <th>Method</th>
                                                   <th>Base Fee</th>
                                                   <th>Service Fee</th>
                                                   <th>Total</th>
                                                   <th>Log ID</th>
                                                   <th>Transaction</th>
                                                   <th>IP</th>
                                                   <th>Date/Time</th>
                                                   <th>Status</th>
                                                   <th>Gateway Status</th>
                                                   <th>Paid By</th>
                                                   {canPerformPaymentActions ? (
                                                     <th className={reportStyles.actionCell}>Action</th>
                                                   ) : null}
                                                 </tr>
                                               </thead>
                                               <tbody>
                                              {paymentLogs.map(
                                                (log, paymentIndex) => {
                                                  const detailPath =
                                                    paymentLogDetailPath(log)
                                                  const amounts =
                                                    paymentLogAmounts(log)
                                                  const paymentActionKey = `${key}-${
                                                    log.payment_log_id ??
                                                    log.pa_id ??
                                                    paymentIndex
                                                  }`
                                                  const gateway = gatewayMetaLookup(
                                                    gatewayMeta,
                                                    log.payment_source,
                                                    log.gateway_status,
                                                  )
                                                  const gatewayTooltip =
                                                    gateway.description ||
                                                    (log.gateway_status ?? '')
                                                  const isLastPaymentRow =
                                                    paymentIndex === paymentLogs.length - 1
                                                  const showEdit = canEditPayment(log)
                                                  const showDelete = canDeletePayment(log)
                                                  return (
                                                    <tr
                                                      key={
                                                        log.payment_log_id ??
                                                        log.pa_id ??
                                                        paymentIndex
                                                      }
                                                    >
                                                      <td className="px-2 py-2">
                                                        {log.type ??
                                                          log.payment_type ??
                                                          '—'}
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        {log.payment_method ??
                                                          log.payment_source ??
                                                          '—'}
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        {formatAmount(amounts.base)}
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        {formatAmount(
                                                          amounts.service,
                                                        )}
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        {formatAmount(amounts.total)}
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        {log.payment_log_id ?? '—'}
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        {log.transaction_id ??
                                                          log.auth_code ??
                                                          '—'}
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        {log.ip_address ?? '—'}
                                                      </td>
                                                      <td className="px-2 py-2 whitespace-nowrap">
                                                        {formatPaymentLogDateTime(
                                                          log.created_at,
                                                        ) || '—'}
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        {log.payment_status ?? '—'}
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        <span className="inline-flex items-center gap-1.5">
                                                          <span>
                                                            {log.gateway_status
                                                              ? gateway.display
                                                              : '—'}
                                                          </span>
                                                          {log.gateway_status &&
                                                          gatewayTooltip ? (
                                                            <SectionInfoIcon
                                                              align="start"
                                                              maxWidth={360}
                                                              iconSize={16}
                                                              isLastRow={isLastPaymentRow}
                                                              aria-label="Gateway status details"
                                                              clampPanelHeight={false}
                                                              className="shrink-0"
                                                              contentClassName="px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap"
                                                              content={gatewayTooltip}
                                                            />
                                                          ) : null}
                                                        </span>
                                                      </td>
                                                      <td className="px-2 py-2">
                                                        <span className="inline-flex items-center gap-1.5">
                                                          <span>{log.paid_by ?? '—'}</span>
                                                          {log.transaction_notes ? (
                                                            <SectionInfoIcon
                                                              align="start"
                                                              maxWidth={360}
                                                              iconSize={16}
                                                              isLastRow={isLastPaymentRow}
                                                              aria-label="Payment notes"
                                                              clampPanelHeight={false}
                                                              className="shrink-0"
                                                              contentClassName="px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap"
                                                              content={log.transaction_notes}
                                                            />
                                                          ) : null}
                                                        </span>
                                                      </td>
                                                      {canPerformPaymentActions ? (
                                                      <td className={`${reportStyles.actionCell} px-2 py-2`}>
                                                        <div className={reportStyles.actionGroup}>
                                                          {!detailPath &&
                                                          !showEdit &&
                                                          !showDelete ? (
                                                            '—'
                                                          ) : (
                                                            <>
                                                              <span className={reportStyles.actionSlot}>
                                                                {detailPath ? (
                                                                  <Link
                                                                    to={detailPath}
                                                                    state={BALANCE_REPORT_PAYMENT_LOG_NAV_STATE}
                                                                    className={reportStyles.actionIconBtn}
                                                                    title="View"
                                                                    aria-label="View payment log"
                                                                  >
                                                                    <Eye aria-hidden />
                                                                  </Link>
                                                                ) : null}
                                                              </span>
                                                              <span className={reportStyles.actionSlot}>
                                                                {showEdit ? (
                                                                  <button
                                                                    type="button"
                                                                    className={reportStyles.actionIconBtn}
                                                                    title="Edit"
                                                                    aria-label="Edit payment adjustment"
                                                                    onClick={() =>
                                                                      onOpenPaymentAdjustment(
                                                                        row,
                                                                        paymentActionKey,
                                                                        log,
                                                                      )
                                                                    }
                                                                    disabled={
                                                                      adjustmentLoadingKey ===
                                                                        paymentActionKey ||
                                                                      deletePaymentLoadingKey ===
                                                                        paymentActionKey
                                                                    }
                                                                  >
                                                                    {adjustmentLoadingKey ===
                                                                    paymentActionKey ? (
                                                                      <Loader2
                                                                        className="animate-spin"
                                                                        aria-hidden
                                                                      />
                                                                    ) : (
                                                                      <Pencil aria-hidden />
                                                                    )}
                                                                  </button>
                                                                ) : null}
                                                              </span>
                                                              <span className={reportStyles.actionSlot}>
                                                                {showDelete ? (
                                                                  <button
                                                                    type="button"
                                                                    className={reportStyles.actionIconBtn}
                                                                    title="Delete"
                                                                    aria-label="Delete payment adjustment"
                                                                    data-testid={`balance-report-delete-payment-${
                                                                      log.pa_id ??
                                                                      log.payment_log_id ??
                                                                      paymentIndex
                                                                    }`}
                                                                    onClick={() =>
                                                                      onDeletePayment(
                                                                        log,
                                                                        paymentActionKey,
                                                                      )
                                                                    }
                                                                    disabled={
                                                                      deletePaymentLoadingKey ===
                                                                        paymentActionKey ||
                                                                      adjustmentLoadingKey ===
                                                                        paymentActionKey
                                                                    }
                                                                  >
                                                                    {deletePaymentLoadingKey ===
                                                                    paymentActionKey ? (
                                                                      <Loader2
                                                                        className="animate-spin"
                                                                        aria-hidden
                                                                      />
                                                                    ) : (
                                                                      <Trash2 aria-hidden />
                                                                    )}
                                                                  </button>
                                                                ) : null}
                                                              </span>
                                                            </>
                                                          )}
                                                        </div>
                                                      </td>
                                                      ) : null}
                                                    </tr>
                                                  )
                                                },
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ) : null}
                              </Fragment>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </ReportTableScroll>
                )}
        <ListPaginationBar
          placement="footer"
          currentPage={pageOptions.current}
          lastPage={pageOptions.last}
          totalRecords={pageOptions.total}
          perPage={params.perPage}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
          testIdPrefix="balance-report"
          summaryLayout="angular"
        />
    </div>
  )
}
